import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Loader2, Paperclip, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { validate, submitEnquiry, errMsg } from "@/lib/enquiry";
import { Field, inputCls, Success } from "@/components/forms/FormBits";

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.csv,.doc,.docx,.dwg,.dxf,.zip";

export const QuoteForm = () => {
  const { t, lang } = useLang();
  const q = t.quote;
  const [params] = useSearchParams();
  const pre = t.services.some((s) => s.slug === params.get("service")) ? [params.get("service")] : [];
  const empty = { company: "", contact_name: "", email: "", phone: "", country: "", services: pre, location: "", cargo_type: "", volume: "", duration: "", message: "" };
  const [v, setV] = useState(empty);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", id: "", error: "" });
  const fileRef = useRef(null);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (slug) => setV((p) => ({ ...p, services: p.services.includes(slug) ? p.services.filter((s) => s !== slug) : [...p.services, slug] }));
  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.size <= MAX_BYTES);
    setFiles((prev) => [...prev, ...picked].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(v, t, { needService: true });
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(`quote-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setState({ status: "sending", id: "", error: "" });
    try {
      const id = await submitEnquiry("quote", lang, v, files);
      setState({ status: "done", id, error: "" });
      setV({ ...empty, services: [] });
      setFiles([]);
    } catch (err) {
      setState({ status: "idle", id: "", error: errMsg(err, q.error) });
    }
  };

  if (state.status === "done") {
    return <Success testId="quote-success" title={q.successTitle} body={q.successBody} refLabel={q.ref} refId={state.id} again={q.another} onAgain={() => setState({ status: "idle", id: "", error: "" })} />;
  }

  const input = (k, label, req = false, extra = {}) => (
    <Field id={`quote-${k}`} label={label} error={errors[k]} required={req}>
      <input id={`quote-${k}`} data-testid={`quote-${k}`} value={v[k]} onChange={set(k)} className={inputCls(errors[k])} {...extra} />
    </Field>
  );
  const locs = [["julyana", q.f.locJulyana], ["sirte", q.f.locSirte], ["unsure", q.f.locUnsure]];

  return (
    <form onSubmit={onSubmit} noValidate data-testid="quote-form" className="grid gap-x-10 gap-y-9 sm:grid-cols-2">
      {input("company", q.f.company, true, { autoComplete: "organization" })}
      {input("contact_name", q.f.contact, true, { autoComplete: "name" })}
      {input("email", q.f.email, true, { type: "email", autoComplete: "email", dir: "ltr" })}
      {input("phone", q.f.phone, false, { type: "tel", autoComplete: "tel", dir: "ltr" })}
      {input("country", q.f.country, false, { autoComplete: "country-name" })}
      <fieldset className="sm:col-span-2" id="quote-services" tabIndex={-1}>
        <legend className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#4A5D75]">{q.f.services}<span className="text-[#A27D17]"> *</span></legend>
        <p className="mb-4 text-sm text-[#4A5D75]">{q.f.servicesHint}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {t.services.map((s) => {
            const on = v.services.includes(s.slug);
            return (
              <button type="button" key={s.slug} onClick={() => toggle(s.slug)} aria-pressed={on} data-testid={`quote-service-${s.slug}`}
                className={`flex items-center gap-3 border px-4 py-3 text-start text-sm transition-colors duration-300 ${on ? "border-[#1E3A5F] bg-[#1E3A5F] text-white" : "border-[#8EA3BC]/60 text-[#1E3A5F] hover:border-[#1E3A5F]"}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center border ${on ? "border-[#C59B27] bg-[#C59B27]" : "border-[#8EA3BC]"}`}>{on && <Check className="h-3.5 w-3.5 text-[#0F2138]" />}</span>
                {s.title}
              </button>
            );
          })}
        </div>
        {errors.services && <p className="mt-2 text-sm text-[#B42318]" role="alert" data-testid="quote-services-error">{errors.services}</p>}
      </fieldset>
      <fieldset className="sm:col-span-2">
        <legend className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#4A5D75]">{q.f.location}</legend>
        <div className="flex flex-wrap gap-3" role="radiogroup">
          {locs.map(([k, label]) => (
            <button type="button" key={k} role="radio" aria-checked={v.location === label} onClick={() => setV((p) => ({ ...p, location: label }))} data-testid={`quote-location-${k}`}
              className={`border px-5 py-3 text-sm transition-colors duration-300 ${v.location === label ? "border-[#1E3A5F] bg-[#1E3A5F] text-white" : "border-[#8EA3BC]/60 text-[#1E3A5F] hover:border-[#1E3A5F]"}`}>{label}</button>
          ))}
        </div>
      </fieldset>
      {input("cargo_type", q.f.cargo, false, { placeholder: q.f.cargoPh })}
      {input("volume", q.f.volume, false, { placeholder: q.f.volumePh })}
      {input("duration", q.f.duration, false, { placeholder: q.f.durationPh })}
      <div className="hidden sm:block" />
      <Field id="quote-message" label={q.f.message} className="sm:col-span-2">
        <textarea id="quote-message" data-testid="quote-message" rows={4} value={v.message} onChange={set("message")} className={`${inputCls(false)} resize-none`} />
      </Field>
      <div className="sm:col-span-2">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#4A5D75]">{q.f.files}</p>
        <label htmlFor="quote-files" className="flex cursor-pointer flex-col items-start gap-2 border border-dashed border-[#8EA3BC] bg-[#F7F9FB] px-6 py-6 transition-colors duration-300 hover:border-[#1E3A5F] sm:flex-row sm:items-center sm:gap-4">
          <Paperclip className="h-5 w-5 text-[#1E3A5F]" aria-hidden="true" />
          <span className="font-semibold text-[#1E3A5F]">{q.f.choose}</span>
          <span className="text-sm text-[#4A5D75]">{q.f.filesHint}</span>
        </label>
        <input ref={fileRef} id="quote-files" data-testid="quote-files" type="file" multiple accept={ACCEPT} onChange={addFiles} className="sr-only" />
        {files.length > 0 && (
          <ul className="mt-3 divide-y divide-[#8EA3BC]/30 border border-[#8EA3BC]/30" data-testid="quote-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                <span className="truncate text-[#0F172A]" dir="ltr">{f.name} <span className="text-[#4A5D75]">({Math.round(f.size / 1024)} KB)</span></span>
                <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} aria-label={q.f.remove} data-testid={`quote-file-remove-${i}`} className="text-[#4A5D75] hover:text-[#B42318]"><X className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col gap-4 border-t border-[#8EA3BC]/40 pt-8 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-serif text-lg text-[#1E3A5F]" data-testid="quote-reply-note">{q.reply}</p>
        <button type="submit" disabled={state.status === "sending"} data-testid="quote-submit" className="btn-brass justify-center disabled:opacity-60">
          {state.status === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />{q.f.sending}</> : <>{q.f.submit}<ArrowRight className="h-4 w-4 rtl:-scale-x-100" /></>}
        </button>
      </div>
      {state.error && <p className="text-sm text-[#B42318] sm:col-span-2" role="alert" data-testid="quote-error">{state.error}</p>}
    </form>
  );
};
